import { IMonitor } from '@types';
import store from '@lib/store';
import { set, when } from 'mobx';
import { v4 as uuid } from 'uuid';

type PerformWithResponse = <T>(name: string, data?: object) => Promise<T>

export interface UserCable extends ActionCable.Channel {
  performWithResponse?: PerformWithResponse
}

interface UserChannelMessage {
  action: 'create' | 'update' | 'delete'
  type: 'Monitor' | 'User'
  attrs: any
  requestId: string
  response: any
}

export interface UserChannel {
  performWithResponse?: PerformWithResponse
}

const resolvers = [];

export function createUserCable(cable) {
  return cable.subscriptions.create(
    {
      channel: 'UserChannel',
    },
    {
      connected() {
        store.userCableReady = true;
      },
      
      disconnected() {
        // Records the fact and nothing else. This used to schedule
        // store.reconnectCable() 3s later on every disconnect — but each
        // reconnect builds a NEW subscription whose disconnected() schedules
        // again, so with a credential the server keeps rejecting the timers
        // compounded: roughly a doubling every three seconds, from every open
        // tab, each one a fresh WebSocket handshake against the API.
        //
        // Recovery is owned by the interval in App.tsx, which polls this flag
        // and calls store.reconnectCable() — one scheduler, with backoff.
        store.userCableReady = false;
      },
      
      async performWithResponse<T>(name: string, data = {}) {
        await when(() => store.userCableReady);

        const requestId = uuid();
        const promise = new Promise((res, rej) => {
          const failTimeout = setTimeout(() => {
            delete resolvers[requestId];
            rej('cable request timeout');
          }, 5000);

          resolvers[requestId] = (response: any) => {
            clearTimeout(failTimeout);
            res(response);
          };

          this.perform(name, { requestId, ...data });
        });
        const response = await promise;

        return response as T;
      },
      
      received({
        action,
        type,
        attrs,
        requestId,
        response,
      }: UserChannelMessage) {
        if (
          action === 'create' &&
          store.monitors.find(({ id }) => id === attrs.id)
        ) {
          // Got create for monitor twice
          return;
        }

        if (action === 'update' && type === 'Monitor') {
          const monitor = store.monitors.find(({ id }) => id === attrs.id);

          // UserChannel broadcasts every monitor this user owns across ALL
          // organizations, while GET /api/monitors is scoped to the current
          // one — so a monitor updating in another organization arrives here
          // with nothing to match, and set(undefined, attrs) threw inside the
          // message handler.
          if (!monitor) return;

          if (attrs.discardedAt) {
            store.monitors.remove(monitor);
          } else {
            set(monitor, attrs);
          }
        } else if (action === 'create' && type === 'Monitor') {
          store.monitors.push(attrs as IMonitor);
        } else if (action === 'update' && type === 'User') {
          if (!store.user) return;
          set(store.user, attrs);
        } else if (requestId) {
          // Guarded: performWithResponse deletes the resolver after 5s, so a
          // reply that arrives at 6s — routine just after a reconnect, while
          // the server is catching up — called undefined and threw inside
          // ActionCable's message handler, taking down processing of every
          // later message on the socket with it.
          const resolve = resolvers[requestId];

          if (resolve) {
            delete resolvers[requestId];
            resolve(response);
          }
        }
      },
      
      rejected() {
        store.userCableReady = false;
      },

    },
  );
}
